import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { 
  ArrowRight, 
  Users, 
  Cpu, 
  Cloud, 
  BarChart3, 
  Settings,
  CheckCircle,
  ChevronRight,
  Quote,
  Zap,
  Shield,
  Globe,
  Building,
  Award
} from 'lucide-react';
import { Button } from '../components/ui/button';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '../components/ui/carousel';
import { Card, CardContent } from '../components/ui/card';
import { contentApi } from '../lib/api';
import { cn } from '../lib/utils';

// Import images from design guidelines
const HERO_IMAGES = {
  main: "https://images.unsplash.com/photo-1770048532712-4fde5ef7eb90?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHwxfHxjb3Jwb3JhdGUlMjB0ZWFtJTIwY29sbGFib3JhdGlvbiUyMG1vZGVybiUyMG9mZmljZSUyMGdsYXNzJTIwd2FsbHN8ZW58MHx8fHwxNzczNzc1OTMxfDA&ixlib=rb-4.1.0&q=85",
  secondary: "https://images.unsplash.com/photo-1600528667634-9cf618cd6488?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1ODR8MHwxfHNlYXJjaHw0fHxjb3Jwb3JhdGUlMjB0ZWFtJTIwY29sbGFib3JhdGlvbiUyMG1vZGVybiUyMG9mZmljZSUyMGdsYXNzJTIwd2FsbHN8ZW58MHx8fHwxNzczNzc1OTMxfDA&ixlib=rb-4.1.0&q=85",
  staffing: "https://images.unsplash.com/photo-1752159684779-0639174cdfac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NTY2NzV8MHwxfHNlYXJjaHwzfHxwcm9mZXNzaW9uYWwlMjBidXNpbmVzcyUyMHBlb3BsZSUyMHNoYWtpbmclMjBoYW5kcyUyMGRlYWx8ZW58MHx8fHwxNzczNzc1OTMzfDA&ixlib=rb-4.1.0&q=85",
  ai: "https://images.unsplash.com/photo-1764336312138-14a5368a6cd3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1OTN8MHwxfHNlYXJjaHwxfHxhcnRpZmljaWFsJTIwaW50ZWxsaWdlbmNlJTIwbmV1cmFsJTIwbmV0d29yayUyMGFic3RyYWN0JTIwYmx1ZSUyMHRlY2h8ZW58MHx8fHwxNzczNzc1OTMyfDA&ixlib=rb-4.1.0&q=85",
  cloud: "https://images.pexels.com/photos/5480781/pexels-photo-5480781.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
  consulting: "https://images.unsplash.com/photo-1565351167686-7a19c5114965?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODh8MHwxfHNlYXJjaHwyfHxkaXZlcnNlJTIwYnVzaW5lc3MlMjB0ZWFtJTIwbWVldGluZyUyMHdoaXRlYm9hcmQlMjBzdHJhdGVneXxlbnwwfHx8fDE3NzM3NzU5MzR8MA&ixlib=rb-4.1.0&q=85"
};

const services = [
  {
    title: "IT Staffing & Talent Solutions",
    description: "Access top-tier IT professionals through our AI-powered talent matching. From contract staffing to permanent placements.",
    icon: Users,
    href: "/services/staffing",
    primary: true,
    image: HERO_IMAGES.staffing,
    features: ["Contract & Full-time", "Executive Search", "Bench Management"]
  },
  {
    title: "AI & Automation Solutions",
    description: "Transform operations with intelligent automation. Custom AI solutions that drive efficiency and innovation.",
    icon: Cpu,
    href: "/services/ai-automation",
    primary: true,
    image: HERO_IMAGES.ai,
    features: ["Process Automation", "ML Integration", "AI Strategy"]
  },
  {
    title: "IT Consulting",
    description: "Strategic technology guidance for enterprise growth. Digital transformation and IT strategy.",
    icon: Settings,
    href: "/services/consulting",
    image: HERO_IMAGES.consulting,
    features: ["Digital Strategy", "Architecture Design", "Tech Assessment"]
  },
  {
    title: "Cloud & DevOps",
    description: "Modernize infrastructure and accelerate delivery. AWS, Azure, GCP expertise.",
    icon: Cloud,
    href: "/services/cloud-devops",
    image: HERO_IMAGES.cloud,
    features: ["Cloud Migration", "DevOps Practices", "Infrastructure"]
  },
  {
    title: "Data & Analytics",
    description: "Turn data into actionable business insights. BI, data engineering, and analytics.",
    icon: BarChart3,
    href: "/services/data-analytics",
    image: HERO_IMAGES.secondary,
    features: ["Data Engineering", "BI Solutions", "Analytics"]
  }
];

const stats = [
  { value: "5000+", label: "Consultants Placed", icon: Users },
  { value: "200+", label: "Enterprise Clients", icon: Building },
  { value: "15+", label: "Years Experience", icon: Award },
  { value: "98%", label: "Client Satisfaction", icon: CheckCircle }
];

const clientLogos = [
  "Fortune 500 Tech", "Healthcare Corp", "Financial Services", 
  "Retail Giant", "Manufacturing Co", "Energy Solutions"
];

export default function HomePage() {
  const [testimonials, setTestimonials] = useState([]);
  const [caseStudies, setCaseStudies] = useState([]);

  useEffect(() => {
    const fetchContent = async () => {
      try {
        const [testimonialsRes, caseStudiesRes] = await Promise.all([
          contentApi.getTestimonials(),
          contentApi.getCaseStudies()
        ]);
        setTestimonials(testimonialsRes.data);
        setCaseStudies(caseStudiesRes.data);
      } catch (err) {
        if (process.env.NODE_ENV === 'development') {
          console.error('Error fetching content:', err);
        }
      }
    };
    fetchContent();
  }, []);

  // Memoize filtered services to avoid recalculation on every render
  const primaryServices = useMemo(() => services.filter(s => s.primary), []);
  const secondaryServices = useMemo(() => services.filter(s => !s.primary), []);

  return (
    <div className="pt-20">
      {/* Hero Section */}
      <section className="relative min-h-[90vh] flex items-center bg-[#0A2540] overflow-hidden" data-testid="hero-section">
        {/* Background Pattern */}
        <div className="absolute inset-0 hero-pattern" />
        
        {/* Hero Image */}
        <div className="absolute right-0 top-0 bottom-0 w-full lg:w-1/2 opacity-30 lg:opacity-100">
          <div 
            className="absolute inset-0 bg-gradient-to-r from-[#0A2540] via-[#0A2540]/50 to-transparent z-10"
          />
          <img 
            src={HERO_IMAGES.main}
            alt="Corporate team collaboration"
            className="h-full w-full object-cover"
            loading="eager"
          />
        </div>

        <div className="container-padding relative z-20 py-20">
          <div className="max-w-2xl">
            <div className="inline-flex items-center bg-white/10 backdrop-blur-sm rounded-full px-4 py-2 text-sm text-white/80 mb-8">
              <Zap className="w-4 h-4 mr-2 text-[#2563EB]" />
              IT Staffing + AI Solutions = Accelerated Growth
            </div>

            <p className="text-2xl md:text-3xl lg:text-4xl text-[#2563EB] font-semibold mb-4 tracking-wide">
              Global IT Associates
            </p>

            <h1 
              className="text-4xl sm:text-5xl lg:text-6xl xl:text-7xl font-bold text-white leading-none tracking-tight"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="hero-title"
            >
              Elite Talent.
              <br />
              <span className="text-[#2563EB]">AI-Driven</span>
              <br />
              Solutions.
            </h1>

            <p className="mt-8 text-lg md:text-xl text-slate-300 leading-relaxed max-w-xl" data-testid="hero-subtitle">
              We combine elite talent with AI-driven solutions to accelerate business growth. 
              Your trusted partner for IT staffing and technology consulting.
            </p>

            <div className="mt-10 flex flex-col sm:flex-row gap-4">
              <Link to="/contact?type=hire_talent">
                <Button 
                  className="btn-accent text-base"
                  data-testid="hero-hire-talent-btn"
                >
                  Hire Top Talent
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact?type=submit_requirement">
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A2540] rounded-full px-8 py-4 text-base font-medium"
                  data-testid="hero-submit-requirement-btn"
                >
                  Submit Your Requirement
                </Button>
              </Link>
            </div>

            {/* Trust Badges */}
            <div className="mt-12 flex flex-wrap items-center gap-6 text-sm text-slate-400">
              <div className="flex items-center">
                <Shield className="w-5 h-5 mr-2 text-[#2563EB]" />
                Enterprise Grade
              </div>
              <div className="flex items-center">
                <Globe className="w-5 h-5 mr-2 text-[#2563EB]" />
                US-Based Operations
              </div>
              <div className="flex items-center">
                <Award className="w-5 h-5 mr-2 text-[#2563EB]" />
                15+ Years Trusted
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="bg-white py-16 border-b border-slate-100" data-testid="stats-section">
        <div className="container-padding">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div 
                key={stat.label} 
                className="text-center"
                data-testid={`stat-item-${index}`}
              >
                <div className="stat-number">{stat.value}</div>
                <div className="stat-label mt-2">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="services-section">
        <div className="container-padding">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <div className="inline-block px-4 py-1 bg-blue-100 text-[#2563EB] rounded-full text-sm font-medium mb-4">
              Our Services
            </div>
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-[#0A2540]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Comprehensive IT Solutions
            </h2>
            <p className="mt-4 text-lg text-slate-600">
              From talent acquisition to digital transformation, we deliver end-to-end solutions for enterprise growth.
            </p>
          </div>

          {/* Primary Services - Large Cards */}
          <div className="grid md:grid-cols-2 gap-8 mb-8">
            {primaryServices.map((service) => (
              <Link 
                key={service.title}
                to={service.href}
                className="card-service group"
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="relative h-48 -mx-8 -mt-8 mb-6 overflow-hidden rounded-t-2xl">
                  <div className="absolute inset-0 bg-gradient-to-t from-white via-transparent to-transparent z-10" />
                  <img 
                    src={service.image}
                    alt={service.title}
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    loading="lazy"
                  />
                </div>
                <div className="service-card-icon mb-4">
                  <service.icon />
                </div>
                <h3 className="text-xl font-semibold text-[#0A2540] mb-2">
                  {service.title}
                </h3>
                <p className="text-slate-600 mb-4">
                  {service.description}
                </p>
                <div className="flex flex-wrap gap-2 mb-4">
                  {service.features.map((feature) => (
                    <span key={feature} className="text-xs bg-blue-50 text-[#2563EB] px-3 py-1 rounded-full">
                      {feature}
                    </span>
                  ))}
                </div>
                <div className="flex items-center text-[#2563EB] font-medium group-hover:translate-x-2 transition-transform">
                  Learn More
                  <ChevronRight className="w-4 h-4 ml-1" />
                </div>
              </Link>
            ))}
          </div>

          {/* Secondary Services - Smaller Cards */}
          <div className="grid md:grid-cols-3 gap-6">
            {secondaryServices.map((service) => (
              <Link 
                key={service.title}
                to={service.href}
                className="card-feature group"
                data-testid={`service-card-${service.title.toLowerCase().replace(/\s+/g, '-')}`}
              >
                <div className="w-12 h-12 rounded-xl bg-slate-100 flex items-center justify-center mb-4 group-hover:bg-[#2563EB] transition-colors">
                  <service.icon className="w-6 h-6 text-slate-600 group-hover:text-white transition-colors" />
                </div>
                <h3 className="text-lg font-semibold text-[#0A2540] mb-2">
                  {service.title}
                </h3>
                <p className="text-sm text-slate-600 mb-3">
                  {service.description}
                </p>
                <div className="flex items-center text-sm text-[#2563EB] font-medium">
                  Explore
                  <ChevronRight className="w-4 h-4 ml-1" />
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Client Logos */}
      <section className="py-16 bg-white border-y border-slate-100" data-testid="clients-section">
        <div className="container-padding">
          <p className="text-center text-sm uppercase tracking-widest text-slate-500 mb-8">
            Trusted by Industry Leaders
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 md:gap-16">
            {clientLogos.map((logo) => (
              <div 
                key={logo}
                className="text-slate-400 font-semibold text-lg client-logo"
                data-testid={`client-logo-${logo.toLowerCase().replace(/\s+/g, '-')}`}
              >
                {logo}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="section-padding bg-white" data-testid="why-choose-section">
        <div className="container-padding">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <div className="inline-block px-4 py-1 bg-blue-100 text-[#2563EB] rounded-full text-sm font-medium mb-4">
                Why Global IT Associates
              </div>
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#0A2540] mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Your Strategic Technology Partner
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                We're not just a staffing company. We're your end-to-end technology partner, 
                combining world-class talent with cutting-edge AI solutions.
              </p>

              <div className="space-y-4">
                {[
                  "AI-powered talent matching for faster, better hires",
                  "Deep enterprise expertise across industries",
                  "Flexible engagement models (Contract, C2C, Full-time)",
                  "End-to-end technology consulting services",
                  "US-based operations with global reach"
                ].map((item, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="w-5 h-5 text-[#2563EB] mr-3 flex-shrink-0 mt-0.5" />
                    <span className="text-slate-700">{item}</span>
                  </div>
                ))}
              </div>

              <div className="mt-10">
                <Link to="/about">
                  <Button className="btn-primary" data-testid="learn-more-btn">
                    Learn More About Us
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
              </div>
            </div>

            <div className="relative">
              <img 
                src={HERO_IMAGES.consulting}
                alt="Team collaboration"
                className="rounded-2xl shadow-2xl"
                loading="lazy"
              />
              <div className="absolute -bottom-8 -left-8 bg-white rounded-xl shadow-xl p-6 max-w-xs">
                <div className="text-4xl font-bold text-[#0A2540]" style={{ fontFamily: 'Outfit, sans-serif' }}>
                  98%
                </div>
                <div className="text-slate-600 mt-1">
                  Client Retention Rate
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="section-padding bg-[#F8FAFC]" data-testid="testimonials-section">
        <div className="container-padding">
          <div className="text-center max-w-3xl mx-auto mb-12">
            <div className="inline-block px-4 py-1 bg-blue-100 text-[#2563EB] rounded-full text-sm font-medium mb-4">
              Client Success Stories
            </div>
            <h2 
              className="text-3xl md:text-4xl font-bold text-[#0A2540]"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              What Our Clients Say
            </h2>
          </div>

          <Carousel
            opts={{
              align: "start",
              loop: true,
            }}
            className="w-full"
          >
            <CarouselContent className="-ml-4">
              {testimonials.map((testimonial, index) => (
                <CarouselItem key={testimonial.id || index} className="pl-4 md:basis-1/2 lg:basis-1/3">
                  <Card className="testimonial-card h-full" data-testid={`testimonial-${index}`}>
                    <CardContent className="p-8">
                      <Quote className="w-10 h-10 text-[#2563EB]/20 mb-4" />
                      <p className="text-slate-700 mb-6 leading-relaxed">
                        "{testimonial.quote}"
                      </p>
                      <div className="flex items-center">
                        <div className="w-12 h-12 rounded-full bg-slate-200 flex items-center justify-center">
                          <Users className="w-6 h-6 text-slate-500" />
                        </div>
                        <div className="ml-4">
                          <div className="font-semibold text-[#0A2540]">
                            {testimonial.author_name}
                          </div>
                          <div className="text-sm text-slate-500">
                            {testimonial.author_role}, {testimonial.company}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </CarouselItem>
              ))}
            </CarouselContent>
            <div className="flex justify-center mt-8 gap-4">
              <CarouselPrevious className="static translate-y-0" />
              <CarouselNext className="static translate-y-0" />
            </div>
          </Carousel>
        </div>
      </section>

      {/* Case Studies Preview */}
      <section className="section-padding bg-white" data-testid="case-studies-section">
        <div className="container-padding">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <div className="inline-block px-4 py-1 bg-blue-100 text-[#2563EB] rounded-full text-sm font-medium mb-4">
                Case Studies
              </div>
              <h2 
                className="text-3xl md:text-4xl font-bold text-[#0A2540]"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Real Results. Real Impact.
              </h2>
            </div>
            <Link to="/case-studies" className="mt-4 md:mt-0">
              <Button variant="outline" className="btn-outline" data-testid="view-all-case-studies-btn">
                View All Case Studies
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </Link>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {caseStudies.slice(0, 3).map((caseStudy, index) => (
              <div 
                key={caseStudy.id || index}
                className="case-study-card"
                data-testid={`case-study-${index}`}
              >
                <div className="relative h-64">
                  <div className="overlay" />
                  <img 
                    src={caseStudy.image_url || HERO_IMAGES.secondary}
                    alt={caseStudy.title}
                    className="w-full h-full object-cover"
                    loading="lazy"
                  />
                  <div className="absolute bottom-0 left-0 right-0 p-6 z-10">
                    <div className="text-sm text-white/80 mb-2">{caseStudy.industry}</div>
                    <h3 className="text-xl font-semibold text-white mb-2">
                      {caseStudy.title}
                    </h3>
                    <div className="flex flex-wrap gap-2">
                      {caseStudy.tags?.slice(0, 3).map((tag) => (
                        <span key={tag} className="text-xs bg-white/20 text-white px-2 py-1 rounded">
                          {tag}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#0A2540]" data-testid="cta-section">
        <div className="container-padding">
          <div className="text-center max-w-3xl mx-auto">
            <h2 
              className="text-3xl md:text-4xl lg:text-5xl font-bold text-white mb-6"
              style={{ fontFamily: 'Outfit, sans-serif' }}
            >
              Ready to Transform Your Business?
            </h2>
            <p className="text-lg text-slate-300 mb-10">
              Whether you need top IT talent or cutting-edge AI solutions, 
              we're here to help you succeed.
            </p>
            <div className="flex flex-col sm:flex-row justify-center gap-4">
              <Link to="/contact?type=hire_talent">
                <Button className="btn-accent text-base" data-testid="cta-hire-talent-btn">
                  Hire Talent Now
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <Link to="/contact">
                <Button 
                  variant="outline"
                  className="border-2 border-white text-white hover:bg-white hover:text-[#0A2540] rounded-full px-8 py-4 text-base font-medium"
                  data-testid="cta-contact-btn"
                >
                  Talk to Our Experts
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
